import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import CompetitorsTabSkeleton from './components/CompetitorsTabSkeleton/CompetitorsTabSkeleton';
import GeneratingLoader from 'components/generatingLoader/generatingLoader';
import OverviewSkeleton from './components/OverviewSkeleton/OverviewSkeleton';
import AndaTableSkeleton from './components/AndaTableSkeleton/AndaTableSkeleton';
import MarketChartsSkeleton from './components/MarketChartsSkeleton/MarketChartsSkeleton';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { FilterMatchMode } from 'primereact/api';
import { Calendar } from 'primereact/calendar';
import { Button } from 'primereact/button';
import { ANDADetailModal } from 'components/shared/ANDADetailModal';
import { ANDAInfoCardData } from 'Interfaces/ProductWikiInterfaces';
import {
  ANDAApplication,
  MarketShareItem,
} from 'Interfaces/ProductWikiInterfaces/competitors.types';
import './CompetitorsTab.styles.scss';
import 'primereact/resources/themes/lara-light-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import { observer } from 'mobx-react';
import { CompetitorsTabProps } from './CompetitorsTab.type';
import useCompetitorsTab from './useCompetitorsTab.logic';
import { productAnalysisStore } from 'stores/ProductWikiStores/ProductAnalysisStore';

// Chart.js imports
import {
  BarController,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  ChartOptions,
  Legend,
  LinearScale,
  LineController,
  LineElement,
  PointElement,
  Title,
  Tooltip,
} from 'chart.js';
import { Chart } from 'react-chartjs-2';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  BarController,
  LineController,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend
);

const CompetitorsTab: React.FC<CompetitorsTabProps> = ({
  competitorData,
  andaData,
}: CompetitorsTabProps) => {
  const {
    filters,
    setFilters,
    andaFilters,
    setAndaFilters,
    effectiveCompetitorData,
    effectiveAndaData,
    effectiveTimeline,
    shouldShowLoader,
    hasCompetitorData,
    hasAndaData,
    currentPage,
    totalCount,
    pageSize,
    andaTimelineRef,
    dateRangeFilterTemplate,
    handleAndaPageChange,
    competitorsStore: _competitorsStore,
    // Section-specific loading states (for split API)
    isOverviewLoading: _isOverviewLoading,
    isAndaLoading,
    isMarketChartsLoading: _isMarketChartsLoading,
    shouldShowOverviewLoader,
    shouldShowAndaLoader,
    shouldShowMarketChartsLoader,
    // AI Insights
    aiInsights,
    isInsightsLoading,
    insightsError,
    // Timeline tooltip
    tooltipData,
    handleCanvasMouseMove,
    handleCanvasMouseLeave,
    // Chart state and data
    chartDateRange: _chartDateRange,
    tempDateRange,
    calendarViewDates,
    selectedCompany,
    marketSalesTrendData,
    filteredMarketSalesTrendData,
    marketShareByVolume,
    marketTrendsChartData,
    // Chart handlers
    handleDateRangeChange,
    handleApplyDateFilter,
    handleCalendarViewDateChange,
    setCalendarViewDates: _setCalendarViewDates,
    handleMarketShareClick,
  } = useCompetitorsTab(productAnalysisStore, competitorData, andaData);

  // Chart.js configuration for Market Sales Trends with dual Y-axes
  const marketTrendsOptions: ChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index' as const,
      intersect: false,
    },
    plugins: {
      legend: {
        position: 'top',
        align: 'end',
        labels: {
          usePointStyle: true,
          padding: 15,
          font: {
            size: 15,
          },
        },
      },
      title: {
        display: false,
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        padding: 12,
        titleColor: '#fff',
        bodyColor: '#fff',
        borderColor: '#666',
        borderWidth: 1,
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          font: {
            size: 15,
          },
        },
      },
      y: {
        type: 'linear' as const,
        display: true,
        position: 'left' as const,
        beginAtZero: true,
        title: {
          display: true,
          text: 'Volume',
          font: {
            size: 15,
            weight: 'bold' as const,
          },
        },
        grid: {
          color: 'rgba(0, 0, 0, 0.05)',
        },
        ticks: {
          font: {
            size: 15,
          },
        },
      },
      y1: {
        type: 'linear' as const,
        display: true,
        position: 'right' as const,
        beginAtZero: true,
        title: {
          display: true,
          text: 'Sales ($)',
          font: {
            size: 15,
            weight: 'bold' as const,
          },
        },
        grid: {
          drawOnChartArea: false,
        },
        ticks: {
          font: {
            size: 15,
          },
        },
      },
    },
  };

  // Color function for market share bars
  const getBarColor = (index: number) => {
    const colors = ['#10b981', '#22c55e', '#4ade80', '#86efac'];
    return colors[index % colors.length];
  };

  // Handle calendar start date change
  const handleStartDateChange = (e: { value: Date | Date[] | null | undefined }) => {
    const selectedDate = e.value as Date;
    if (selectedDate) {
      handleDateRangeChange(selectedDate, tempDateRange.endDate);
      handleCalendarViewDateChange({
        ...calendarViewDates,
        startViewDate: selectedDate,
      });
    } else {
      handleDateRangeChange(null, tempDateRange.endDate);
      handleCalendarViewDateChange({
        ...calendarViewDates,
        startViewDate: null,
      });
    }
  };

  // Handle calendar end date change
  const handleEndDateChange = (e: { value: Date | Date[] | null | undefined }) => {
    const selectedDate = e.value as Date;
    if (selectedDate) {
      handleDateRangeChange(tempDateRange.startDate, selectedDate);
      handleCalendarViewDateChange({
        ...calendarViewDates,
        endViewDate: selectedDate,
      });
    } else {
      handleDateRangeChange(tempDateRange.startDate, null);
      handleCalendarViewDateChange({
        ...calendarViewDates,
        endViewDate: null,
      });
    }
  };

  // Prevent calendar trigger buttons from causing page scroll
  useEffect(() => {
    const handleCalendarButtons = () => {
      const calendarTriggers = document.querySelectorAll(
        '.chart-date-picker .p-calendar-trigger button'
      );
      calendarTriggers.forEach(button => {
        if (button instanceof HTMLButtonElement) {
          button.type = 'button';
        } else {
          button.setAttribute('type', 'button');
        }
      });
    };

    handleCalendarButtons();

    const observer = new MutationObserver(() => {
      setTimeout(handleCalendarButtons, 0);
    });
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class'],
    });

    return () => {
      observer.disconnect();
    };
  }, [selectedCompany]);

  // State for ANDA detail modal
  const [modalVisible, setModalVisible] = useState<boolean>(false);
  const [selectedAnda, setSelectedAnda] = useState<ANDAInfoCardData | null>(null);

  /**
   * Transform ANDAApplication to ANDAInfoCardData format for the modal
   */
  const transformToModalData = (anda: ANDAApplication): ANDAInfoCardData => {
    const isDiscontinued = anda.marketingStatus?.toLowerCase() === 'discontinued';
    const color = isDiscontinued ? '#4b5563' : '#10b981';

    return {
      title: `${anda.applicationNumber} - ${anda.activeIngredient} - ${anda.strength} - ${anda.route} - ${anda.dosageForm}`,
      date: anda.effectiveDate || 'N/A',
      card_description: `${anda.applicantHolder} - ${anda.strength}`,
      description: `${anda.applicantHolder} - ${anda.strength}`,
      tags: [
        { status: anda.marketStatus, type: 'normal' },
        { applicant: anda.applicantHolder, type: 'normal' },
      ],
      priority: 'normal',
      application_number: anda.applicationNumber,
      approval_date: anda.effectiveDate || null,
      applicant: anda.applicantHolder,
      concept_id: null,
      color: color,
      link: anda.andaLink || null,
      effective_date: anda.effectiveDate || null,
      marketing_status: anda.marketingStatus || null,
      last_submission_date: anda.lastSubmissionDate || null,
      last_submission_type: anda.lastSubmissionType || null,
      history: anda.history || [],
    };
  };

  /**
   * Handle row click to open modal
   */
  const handleAndaRowClick = (rowData: ANDAApplication) => {
    const modalData = transformToModalData(rowData);
    setSelectedAnda(modalData);
    setModalVisible(true);
  };

  /**
   * Handle modal close
   */
  const handleModalHide = () => {
    setModalVisible(false);
    setSelectedAnda(null);
  };

  /**
   * Format date from 'YYYY-MM-DD' to 'Dec 10, 20' format
   */
  const formatSubmissionDate = (dateString: string | null | undefined): string => {
    if (!dateString) return 'N/A';

    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString; // Return original if invalid

      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const month = monthNames[date.getMonth()];
      const day = date.getDate();
      const year = date.getFullYear().toString().slice(-2); // Get last 2 digits

      return `${month} ${day}, ${year}`;
    } catch {
      return dateString; // Return original if parsing fails
    }
  };

  /**
   * Application Number body template with dynamic coloring and link
   */
  const applicationNumberBodyTemplate = (rowData: ANDAApplication) => {
    const isDiscontinued = rowData.marketingStatus?.toLowerCase() === 'discontinued';
    const colorClass = isDiscontinued ? 'anda-link-discontinued' : 'anda-link-active';

    if (rowData.andaLink) {
      return (
        <a
          href={rowData.andaLink}
          target="_blank"
          rel="noopener noreferrer"
          className={`anda-link ${colorClass}`}
          onClick={e => e.stopPropagation()}
        >
          {rowData.applicationNumber}
        </a>
      );
    }

    return <span className={`anda-text ${colorClass}`}>{rowData.applicationNumber}</span>;
  };

  return (
    <section className="content-section">
      <div className="section-header">
        <h3 className="section-title">Competitors & Applications</h3>
        {/* <span className="section-subtitle">
          Analysis based on 12M rolling data | Last updated: Dec 15, 2024
        </span> */}
      </div>

      {/* ANDA Timeline - First thing user sees */}
      {!shouldShowOverviewLoader && effectiveTimeline && effectiveTimeline.length > 0 && (
        <div className="anda-timeline">
          <canvas
            ref={andaTimelineRef}
            height={180}
            onMouseMove={handleCanvasMouseMove}
            onMouseLeave={handleCanvasMouseLeave}
          ></canvas>
        </div>
      )}

      {/* Market Sales Charts Section - positioned below ANDA Timeline */}
      {shouldShowMarketChartsLoader ? (
        <MarketChartsSkeleton />
      ) : (
        <div className="charts-section">
          <div className="chartsGrid">
            {/* Market Trends Chart */}
            <div className="chartBox">
              <div className="chartBoxHeader">
                <h4>
                  Market Sales Trends by Agency
                  {selectedCompany && <span className="companyName"> - {selectedCompany}</span>}
                </h4>
              </div>
              <div className="chartControls">
                <div className="dateRangePicker">
                  <label style={{ fontSize: '1rem', marginRight: '0.5rem' }}>
                    Date Range:
                  </label>
                  <Calendar
                    key={`start-date-${selectedCompany || 'all'}`}
                    value={tempDateRange.startDate}
                    viewDate={
                      calendarViewDates.startViewDate || tempDateRange.startDate || undefined
                    }
                    onChange={handleStartDateChange}
                    placeholder="Start Date"
                    dateFormat="mm/dd/yy"
                    yearNavigator
                    monthNavigator
                    yearRange="2010:2030"
                    showIcon
                    icon="pi pi-calendar"
                    appendTo={typeof document !== 'undefined' ? document.body : undefined}
                    style={{ width: '140px', fontSize: '0.75rem' }}
                    showOnFocus={false}
                    className="chart-date-picker"
                  />
                  <span style={{ margin: '0 0.5rem' }}>-</span>
                  <Calendar
                    key={`end-date-${selectedCompany || 'all'}`}
                    value={tempDateRange.endDate}
                    viewDate={
                      calendarViewDates.endViewDate || tempDateRange.endDate || undefined
                    }
                    onChange={handleEndDateChange}
                    placeholder="End Date"
                    dateFormat="mm/dd/yy"
                    yearNavigator
                    monthNavigator
                    yearRange="2010:2030"
                    showIcon
                    icon="pi pi-calendar"
                    appendTo={typeof document !== 'undefined' ? document.body : undefined}
                    style={{ width: '140px', fontSize: '0.75rem' }}
                    showOnFocus={false}
                    className="chart-date-picker"
                  />
                  <Button
                    label="Apply Filter"
                    icon="pi pi-filter"
                    onClick={handleApplyDateFilter}
                    style={{
                      marginLeft: '0.5rem',
                      padding: '0.625rem 0.75rem',
                      fontSize: '1rem',
                      height: '2.5rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      backgroundColor: '#ffffff',
                      color: '#374151',
                    }}
                  />
                </div>
              </div>
              <div className="chartContent" style={{ height: '500px', width: '100%', position: 'relative' }}>
                {filteredMarketSalesTrendData.length > 0 || marketSalesTrendData.length > 0 ? (
                  <Chart
                    type="bar"
                    data={marketTrendsChartData}
                    options={marketTrendsOptions}
                  />
                ) : (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: '100%',
                      color: '#6b7280',
                    }}
                  >
                    <div style={{ textAlign: 'center' }}>
                      <i
                        className="pi pi-chart-line"
                        style={{ fontSize: '2rem', marginBottom: '0.5rem' }}
                      ></i>
                      <p>No market sales trend data available.</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Market Share by Volume Chart */}
            <div className="chartBox">
              <div className="chartBoxHeader">
                <h4>Market Share by Volume, Labeler</h4>
              </div>
              <div className="chartSubtitle">
                <span>Click on a company to view their specific sales data</span>
              </div>
              <div
                className="marketShareList"
                style={{
                  height: '500px',
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  position: 'relative'
                }}
              >
                {marketShareByVolume.length > 0 ? (
                  marketShareByVolume.map((item: MarketShareItem, idx: number) => (
                    <div
                      key={idx}
                      className={`shareItem ${selectedCompany === item.vendor ? 'selected' : ''}`}
                      onClick={() => handleMarketShareClick(item.vendor)}
                      style={{ cursor: 'pointer' }}
                    >
                      <span className="vendorName">{item.vendor}</span>
                      {item.share > 0 ? (
                        <div className="shareBarContainer">
                          <div className="shareBar">
                            <div
                              className="shareFill"
                              style={{
                                width: `${item.share}%`,
                                background: getBarColor(idx),
                                position: 'relative',
                                minWidth: item.share < 8 ? '60px' : 'auto',
                              }}
                            >
                              <span
                                className="sharePercentage"
                                style={{
                                  position: 'absolute',
                                  left: '50%',
                                  top: '50%',
                                  transform: 'translate(-50%, -50%)',
                                  color: item.share > 5 ? '#ffffff' : '#1f2937',
                                  fontWeight: 700,
                                  fontSize: '0.875rem',
                                  textShadow:
                                    item.share > 5
                                      ? '0 1px 3px rgba(0,0,0,0.5)'
                                      : '0 1px 2px rgba(255,255,255,0.8)',
                                  whiteSpace: 'nowrap',
                                  zIndex: 2,
                                }}
                              >
                                {item.share.toFixed(2)}%
                              </span>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <span style={{ color: '#9ca3af', fontSize: '0.875rem' }}>0.00%</span>
                      )}
                    </div>
                  ))
                ) : (
                  <div style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>
                    <i
                      className="pi pi-chart-bar"
                      style={{ fontSize: '2rem', marginBottom: '0.5rem' }}
                    ></i>
                    <p>No market share data available.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Timeline Tooltip - rendered via Portal to document.body for proper fixed positioning */}
      {tooltipData.visible &&
        tooltipData.event &&
        ReactDOM.createPortal(
          <div
            className="timeline-tooltip"
            style={{
              position: 'fixed',
              left: tooltipData.x,
              top: tooltipData.y,
              transform: 'translate(-50%, -100%)',
              zIndex: 9999,
            }}
          >
            <div
              className="tooltip-color-indicator"
              style={{ backgroundColor: tooltipData.event.color }}
            />
            <div className="tooltip-content">
              <div className="tooltip-name">{tooltipData.event.name}</div>
              <div className="tooltip-year">Year: {tooltipData.event.year}</div>
              {tooltipData.event.details && (
                <div className="tooltip-details">{tooltipData.event.details}</div>
              )}
            </div>
            <div className="tooltip-arrow" />
          </div>,
          document.body
        )}

      {/* Entry Barriers Section - Uses overview loading state */}
      {shouldShowOverviewLoader ? (
        <div className="entry-barriers-skeleton-wrapper">
          <OverviewSkeleton />
        </div>
      ) : (
        effectiveCompetitorData?.barriers && (
          <div className="entry-barriers">
            {effectiveCompetitorData.barriers.map((barrier, index) => (
              <div key={index} className="barrier-metric">
                <div className="barrier-metric-value">{barrier.metric}</div>
                <div className="barrier-metric-label">{barrier.label}</div>
              </div>
            ))}
          </div>
        )
      )}

      <div className="section-content">
        {shouldShowLoader ? (
          <CompetitorsTabSkeleton />
        ) : (
          <>
            {/* Manufacturers Table - uses overview loading state */}
            {shouldShowOverviewLoader ? null : hasCompetitorData ? (
              <>
                <div className="anda-section">
                  <div className="anda-header">
                    <h4 className="anda-header-title">
                      <i className="fas fa-industry"></i>
                      <span className="anda-header-title-text">Competitors</span>
                    </h4>
                  </div>
                  <div className="competitor-datatable-wrapper">
                    <DataTable
                      value={effectiveCompetitorData?.manufacturers || []}
                      filters={filters}
                      onFilter={e => setFilters(e.filters)}
                      filterDisplay="row"
                      globalFilterFields={[
                        'name',
                        'labeler',
                        'ndcCount',
                        'price',
                        'ranking',
                        'averageWac',
                        'entryDate',
                      ]}
                      emptyMessage="No competitor data available"
                      className="competitor-datatable"
                      scrollable
                      scrollHeight="800px"
                    >
                      <Column
                        field="name"
                        header="Manufacturer"
                        filter
                        filterPlaceholder="Search Manufacturer"
                        showFilterMenu={false}
                        showClearButton={true}
                        filterMatchMode={FilterMatchMode.CONTAINS}
                      />
                      <Column
                        field="labeler"
                        header="Labeler"
                        filter
                        filterPlaceholder="Search Labeler"
                        showFilterMenu={false}
                        showClearButton={true}
                        filterMatchMode={FilterMatchMode.CONTAINS}
                      />
                      <Column
                        field="ndcCount"
                        header="NDC Count"
                        filter
                        filterPlaceholder="Search NDC Count"
                        showFilterMenu={false}
                        showClearButton={true}
                        filterMatchMode={FilterMatchMode.CONTAINS}
                      />
                      <Column
                        field="price"
                        header="Price"
                        filter
                        filterPlaceholder="Search Price"
                        showFilterMenu={false}
                        showClearButton={true}
                        filterMatchMode={FilterMatchMode.CONTAINS}
                        body={rowData =>
                          rowData.price !== null && rowData.price !== undefined
                            ? `$${Number(rowData.price).toFixed(2)}`
                            : 'N/A'
                        }
                      />
                      <Column
                        field="ranking"
                        header="Price Ranking"
                        filter
                        filterPlaceholder="Search Ranking"
                        showFilterMenu={false}
                        showClearButton={true}
                        filterMatchMode={FilterMatchMode.CONTAINS}
                        body={rowData => (
                          <span className={`ranking-badge`}>
                            {rowData.ranking ? rowData.ranking : 'N/A'}
                          </span>
                        )}
                      />
                      <Column
                        field="averageWac"
                        header="Average WAC"
                        filter
                        filterPlaceholder="Search Average WAC"
                        showFilterMenu={false}
                        showClearButton={true}
                        filterMatchMode={FilterMatchMode.CONTAINS}
                        body={rowData =>
                          rowData.averageWac !== null ? `$${rowData.averageWac}` : 'N/A'
                        }
                      />
                      <Column
                        field="entryDate"
                        header="Entry Date"
                        filter
                        filterPlaceholder="Date Range"
                        showFilterMenu={false}
                        showClearButton={true}
                        filterMatchMode={FilterMatchMode.CUSTOM}
                        filterElement={dateRangeFilterTemplate}
                      />
                    </DataTable>
                  </div>
                </div>
              </>
            ) : (
              <div className="no-data-message">No data available</div>
            )}

            {/* ANDA Section - uses ANDA-specific loading state */}
            <div className="anda-section">
              <div className="anda-header">
                <h4 className="anda-header-title">
                  <i className="fas fa-file-medical-alt"></i>
                  <span className="anda-header-title-text">Applications</span>
                </h4>
              </div>

              <div className="anda-table-container">
                {/* Show skeleton only during ANDA loading (pagination) */}
                {shouldShowAndaLoader ? (
                  <AndaTableSkeleton />
                ) : (
                  <div className="anda-datatable-wrapper" style={{ position: 'relative' }}>
                    {/* Show inline loader during pagination when data is already present */}
                    {isAndaLoading && hasAndaData && (
                      <div className="anda-loading-overlay">
                        <i className="pi pi-spin pi-spinner" style={{ fontSize: '2rem', color: '#5b5fd6' }}></i>
                      </div>
                    )}
                    <DataTable
                      value={effectiveAndaData?.applications || []}
                      lazy
                      paginator
                      rows={pageSize}
                      totalRecords={totalCount}
                      onPage={handleAndaPageChange}
                      first={(currentPage - 1) * pageSize}
                      filters={andaFilters}
                      onFilter={e => setAndaFilters(e.filters)}
                      filterDisplay="row"
                      globalFilterFields={[
                        'applicationNumber',
                        'marketStatus',
                        'activeIngredient',
                        'dosageForm',
                        'route',
                        'strength',
                        'rld',
                        'rs',
                        'applicantHolder',
                        'lastSubmissionType',
                        'lastSubmissionDate',
                      ]}
                      emptyMessage="No ANDA data available"
                      className="anda-datatable"
                      scrollable
                      scrollHeight="1080px"
                      selectionMode="single"
                      onRowClick={e => handleAndaRowClick(e.data as ANDAApplication)}
                      rowClassName={() => 'clickable-row'}
                    >
                      <Column
                        field="applicationNumber"
                        header="Appl. No."
                        filter
                        filterPlaceholder="Search Appl. No."
                        showFilterMenu={false}
                        showClearButton={true}
                        filterMatchMode={FilterMatchMode.CONTAINS}
                        body={applicationNumberBodyTemplate}
                        style={{ minWidth: '150px' }}
                      />
                      <Column
                        field="marketStatus"
                        header="Market Status"
                        filter
                        filterPlaceholder="Search Market Status"
                        showFilterMenu={false}
                        showClearButton={true}
                        filterMatchMode={FilterMatchMode.CONTAINS}
                        style={{ minWidth: '150px' }}
                      />
                      <Column
                        field="activeIngredient"
                        header="Active Ingredient"
                        filter
                        filterPlaceholder="Search Active Ingredient"
                        showFilterMenu={false}
                        showClearButton={true}
                        filterMatchMode={FilterMatchMode.CONTAINS}
                        style={{ minWidth: '150px' }}
                      />
                      <Column
                        field="dosageForm"
                        header="Dosage Form"
                        filter
                        filterPlaceholder="Search Dosage Form"
                        showFilterMenu={false}
                        showClearButton={true}
                        filterMatchMode={FilterMatchMode.CONTAINS}
                        style={{ minWidth: '150px' }}
                      />
                      <Column
                        field="route"
                        header="Route"
                        filter
                        filterPlaceholder="Search Route"
                        showFilterMenu={false}
                        showClearButton={true}
                        filterMatchMode={FilterMatchMode.CONTAINS}
                        style={{ minWidth: '150px' }}
                      />
                      <Column
                        field="strength"
                        header="Strength"
                        filter
                        filterPlaceholder="Search Strength"
                        showFilterMenu={false}
                        showClearButton={true}
                        filterMatchMode={FilterMatchMode.CONTAINS}
                        style={{ minWidth: '150px' }}
                      />
                      <Column
                        field="rld"
                        header="RLD"
                        filter
                        filterPlaceholder="Search RLD"
                        showFilterMenu={false}
                        showClearButton={true}
                        filterMatchMode={FilterMatchMode.CONTAINS}
                        style={{ minWidth: '150px' }}
                      />
                      <Column
                        field="rs"
                        header="RS"
                        filter
                        filterPlaceholder="Search RS"
                        showFilterMenu={false}
                        showClearButton={true}
                        filterMatchMode={FilterMatchMode.CONTAINS}
                        style={{ minWidth: '150px' }}
                      />
                      <Column
                        field="applicantHolder"
                        header="Applicant Holder"
                        filter
                        filterPlaceholder="Search Applicant Holder"
                        showFilterMenu={false}
                        showClearButton={true}
                        filterMatchMode={FilterMatchMode.CONTAINS}
                        style={{ minWidth: '150px' }}
                      />
                      <Column
                        field="lastSubmissionType"
                        header="Last Submission Type"
                        filter
                        filterPlaceholder="Search Submission Type"
                        showFilterMenu={false}
                        showClearButton={true}
                        filterMatchMode={FilterMatchMode.CONTAINS}
                        style={{ minWidth: '160px' }}
                      />
                      <Column
                        field="lastSubmissionDate"
                        header="Last Submission Date"
                        filter
                        filterPlaceholder="Date Range"
                        showFilterMenu={false}
                        showClearButton={true}
                        filterMatchMode={FilterMatchMode.CUSTOM}
                        filterElement={dateRangeFilterTemplate}
                        body={rowData => formatSubmissionDate(rowData.lastSubmissionDate)}
                        style={{ minWidth: '250px' }}
                      />
                    </DataTable>
                  </div>
                )}
              </div>
            </div>

            {/* AI-Powered Competitive Intelligence Summary */}
            {isInsightsLoading ? (
              <div className="narrative-box ai-insights-box">
                <h4 className="narrative-box-text-title">Competitive Intelligence Summary</h4>
                <div className="narrative-content">
                  <GeneratingLoader />
                </div>
              </div>
            ) : insightsError ? (
              <div className="narrative-box ai-insights-box error">
                <h4 className="narrative-box-text-title">Competitive Intelligence Summary</h4>
                <div className="narrative-content">
                  <div className="insights-error-container">
                    <i className="fas fa-exclamation-triangle insights-error-icon" />
                    <div className="insights-error-details">
                      <p className="insights-error-message">{insightsError}</p>
                    </div>
                  </div>
                </div>
              </div>
            ) : aiInsights && aiInsights.length > 0 ? (
              <div className="narrative-box ai-insights-box">
                <h4 className="narrative-box-text-title">Competitive Intelligence Summary</h4>
                <div className="narrative-content">
                  <ul className="ai-insights-list">
                    {aiInsights.map((insight, index) => (
                      <li key={index} className="insight-item">
                        <span className="narrative-box-text-item">{insight.bullet}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ) : (
              <div className="narrative-box ai-insights-box no-data">
                <h4 className="narrative-box-text-title">Competitive Intelligence Summary</h4>
                <div className="narrative-content">
                  <div className="insights-no-data">
                    <span className="no-data-icon">📊</span>
                    <p className="no-data-message">No AI insights available at the moment.</p>
                    <p className="no-data-hint">
                      Insights will be generated based on competitive intelligence data analysis.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* ANDA Detail Modal */}
      <ANDADetailModal visible={modalVisible} onHide={handleModalHide} data={selectedAnda} />
    </section>
  );
};

export default observer(CompetitorsTab);
